import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUserModule } from '@/hooks/useUserModule';
import { updateModuleProgress, completeUserModule, completeDocumentModule, completeEnhancedSurvey } from '@/api/module';
import { useAuth } from '@/contexts/AuthContext';
import { useNoGuard } from '@/hooks/useNoGuard';
import Profile from '@/pages/Profile';
import Updates from '@/pages/Updates';
import DeclarativeSurveyModule from './DeclarativeSurveyModule';
import DynamicModuleScreen from './DynamicModuleScreen';
import DynamicChatModule from './DynamicChatModule';
import DynamicChatModuleEnhanced from './chat/DynamicChatModuleEnhanced';
import DynamicDashboard from './DynamicDashboard';
import StructuredDocumentModule from './StructuredDocumentModule';
import EnhancedSurveyModule from './EnhancedSurveyModule';
import ModuleLoadingState from './ModuleLoadingState';
import type { ChatModuleData } from '@/types/chatCompletion';

/**
 * Declarative Module Router - Routes based on module.screenType
 * Single /module route that renders appropriate content
 */
function DeclarativeModuleRouter() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const noGuard = useNoGuard();
  const { authenticated, isLoading: authLoading, user } = useAuth();
  const { data: userModule, isLoading: moduleLoading, error } = useCurrentUserModule();

  useEffect(() => {
    // In noguard mode, don't redirect
    if (noGuard) return;

    if (authLoading || moduleLoading) return;

    if (!authenticated) {
      navigate('/');
      return;
    }

    // If no active module and not loading, check user's onboarding status
    if (!moduleLoading && !userModule) {
      console.log('No current module found, checking user onboarding status:', user?.onboardingStatus);
      
      // Route based on onboarding status instead of assuming /updates
      if (user?.onboardingStatus === 'welcome') {
        navigate('/');
      } else if (user?.onboardingStatus === 'profile') {
        navigate('/profile');
      } else {
        // For completed users or unknown states, go to updates
        navigate('/updates');
      }
      return;
    }
  }, [authenticated, authLoading, moduleLoading, userModule, navigate, noGuard, user]);

  // Loading state
  if (authLoading || moduleLoading) {
    return <ModuleLoadingState message="Loading your next activity..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load module</p>
          <button 
            onClick={() => navigate('/updates')}
            className="text-primary hover:underline"
          >
            Return to dashboard
          </button>
        </div>
      </div>
    );
  }

  // No module state
  if (!userModule || !userModule.module) {
    if (!noGuard) {
      navigate('/updates');
      return null;
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>No active module</p>
      </div>
    );
  }

  // Route based on screenType
  const { module, progressCheckpoint = 0 } = userModule;
  
  console.log('DeclarativeModuleRouter:', {
    screenType: module.screenType,
    progressCheckpoint,
    moduleId: module.id,
    userModuleId: userModule.id
  });

  switch (module.screenType) {
    case 'survey':
      return (
        <DeclarativeSurveyModule 
          key={`survey-${userModule.id}`}  // Force re-render when module changes
          userModule={userModule}
          startAt={progressCheckpoint}
        />
      );
      
    case 'survey_intro':
      // Use DynamicModuleScreen for survey intro to use backend-provided text
      return (
        <DynamicModuleScreen
          key={`survey-intro-${module.id}`}  // Force re-render when module changes
          module={module}
          onPrimaryAction={async () => {
            // Complete the intro module and advance to the actual survey module
            try {
              console.log('Starting survey - completing intro module:', userModule.id);
              await completeUserModule(userModule.id);
              // Invalidate queries to get the next module
              await queryClient.invalidateQueries({ queryKey: ['current-user-module'] });
              await queryClient.invalidateQueries({ queryKey: ['user-modules'] });
            } catch (error) {
              console.error('Failed to start survey:', error);
              // Stay on current page
            }
          }}
          onExit={() => navigate('/updates')}
        />
      );
      
    case 'profile':
      return <Profile />;
      
    case 'chat': {
      // Check if module has completion criteria enabled
      const chatData: ChatModuleData = module.data ? JSON.parse(module.data) : {};
      const hasCompletionFeatures = chatData.completionCriteria || chatData.summaryConfig;
      
      const ChatComponent = hasCompletionFeatures ? DynamicChatModuleEnhanced : DynamicChatModule;
      
      return (
        <ChatComponent 
          key={`chat-${module.id}`}  // Force re-render when module changes
          module={module}
          onComplete={async () => {
            try {
              await completeUserModule(userModule.id);
              // Invalidate queries to get the next module
              await queryClient.invalidateQueries({ queryKey: ['current-user-module'] });
              await queryClient.invalidateQueries({ queryKey: ['user-modules'] });
            } catch (error) {
              console.error('Failed to complete chat module:', error);
              navigate('/updates');
            }
          }}
        />
      );
    }
      
    case 'welcome':
      return <Updates />;
      
    case 'dashboard':
      return <DynamicDashboard key={`dashboard-${module.id}`} module={module} />;
      
    case 'text':
      // For generic content modules, use DynamicModuleScreen
      return (
        <DynamicModuleScreen
          key={`text-${module.id}`}  // Force re-render when module changes
          module={module}
          onPrimaryAction={() => {
            // Complete module and navigate to next
            navigate('/updates');
          }}
          onExit={() => navigate('/updates')}
        />
      );
      
    case 'structured_document':
      return (
        <StructuredDocumentModule
          key={`structured-document-${module.id}`}  // Force re-render when module changes
          module={module}
          onComplete={async () => {
            try {
              await completeDocumentModule(userModule.id);
              // Invalidate queries to get the next module
              await queryClient.invalidateQueries({ queryKey: ['current-user-module'] });
              await queryClient.invalidateQueries({ queryKey: ['user-modules'] });
            } catch (error) {
              console.error('Failed to complete document module:', error);
              navigate('/updates');
            }
          }}
        />
      );
      
    case 'enhanced_survey':
      return (
        <EnhancedSurveyModule
          key={`enhanced-survey-${userModule.id}`}  // Force re-render when module changes
          userModule={userModule}
          startAt={progressCheckpoint}
        />
      );
      
    default:
      console.warn(`Unknown screenType: ${module.screenType}`);
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Unknown module type: {module.screenType}
            </p>
            <button 
              onClick={() => navigate('/updates')}
              className="text-primary hover:underline"
            >
              Return to dashboard
            </button>
          </div>
        </div>
      );
  }
}

export default DeclarativeModuleRouter;